import {useMemo} from 'react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {useMergeabilityStatus} from '@github-ui/mergebox/use-mergeability-status'
import type {Channels} from '@github-ui/mergebox/use-mergeability-live-updates'
import type {ActionabilityState} from './actionability-types'
import {deriveActionability} from './derive-actionability'

interface UseActionabilityOptions {
  /** Author login from the layout route header data */
  authorLogin: string
  /** Head SHA for status checks query key (from mergeStatusButtonData) */
  headSha: string
  /** Live-update WebSocket channels from mergeStatusButtonData */
  channels: Channels | null
  /** Whether the viewer has a pending review request on this PR */
  viewerIsRequestedReviewer: boolean
}

/**
 * Derives `ActionabilityState` from live-updating mergebox and status-checks
 * data via `useMergeabilityStatus`, which subscribes to WebSocket channels
 * for automatic refetching.
 *
 * Returns `null` while data is still loading, or when no actionable
 * state applies.
 */
export interface UseActionabilityResult {
  actionability: ActionabilityState | null
  isLoading: boolean
}

export function useActionability({
  authorLogin,
  headSha,
  channels,
  viewerIsRequestedReviewer,
}: UseActionabilityOptions): UseActionabilityResult {
  const viewerLogin = getCurrentUserLogin()

  const {status, isLoading} = useMergeabilityStatus({
    headSha,
    channels,
  })

  const actionability = useMemo(() => {
    if (!status) return null

    return deriveActionability({
      status,
      authorLogin,
      viewerLogin,
      viewerIsRequestedReviewer,
    })
  }, [status, authorLogin, viewerLogin, viewerIsRequestedReviewer])

  return {actionability, isLoading}
}
